import type { AxiosInstance } from 'axios';
import type {
  CommonListArgs,
  GetEntityByIdParams,
  IsoDate,
  Nullable,
  PaginatedList,
  PartnerShort,
  RoleShort,
  SortDirection,
} from './types.ts';
import type { RolesEnum } from '../assets/enums/roles.enum.ts';
import type { InviteUserDto, UpdateUserFormDto } from '../assets/forms/form-schemes.ts';
import { prepareOrderingForBackend } from '../assets/helpers/utils/prepare-ordering-for-backend.ts';
import { handleAddPhotoToFormData } from '../assets/helpers/utils/handle-add-photo-to-form-data.ts';

export class UsersApi {
  constructor(private instance: AxiosInstance) {
  }

  public async getUsers(params: UsersListArgs) {
    return this.instance.get<PaginatedList<User>>('/users/', {
      params: {
        page: params.page,
        search: params.search,
        ordering: prepareOrderingForBackend(params.sortBy, params.sortDirection),
        partner_id: params.partner_id,
      },
    }).then(res => res.data);
  };

  public async getUserById(params: GetEntityByIdParams) {
    return this.instance.get<User>(`/users/${params.id}/`).then(res => res.data);
  };

  public async deleteUser(params: GetEntityByIdParams) {
    return this.instance.delete<User>(`/users/${params.id}/`).then(res => res.data);
  };

  public async invite(dto: InviteUserDto) {
    return this.instance.post<InviteUserDto & GetEntityByIdParams>(`/users/invite/`, dto).then(res => res.data);
  };

  public async getInvitation(params: GetEntityByIdParams) {
    return this.instance.get<InvitationInfo>(`/users/invite/${params.id}`).then(res => res.data);
  };

  public async updateUser(dto: UpdateUserDto) {
    return this.instance.patch<User>(`/users/${dto.id}/`, this.prepareFormDataToUpdateUser(dto)).then(res => res.data);
  }

  private prepareFormDataToUpdateUser(dto: UpdateUserDto) {
    const formData = new FormData();
    formData.append('email', dto.email);
    formData.append('username', dto.username);
    if (dto?.partner) {
      formData.append('partner', dto.partner);
    }

    handleAddPhotoToFormData({
      formData,
      value:dto?.photo,
      propName:'photo'
    })

    return formData;
  }
}

export type UpdateUserDto = UpdateUserFormDto & GetEntityByIdParams;

export type InvitationInfo = {
  invite_token: string;
  email: string;
  role: RoleShort;
  partner: PartnerShort
  invited_by_username: Nullable<string>
}
export type UsersListArgs = {
  search?: string;
  sortBy?: string;
  sortDirection?: SortDirection;
  partner_id?: string;
} & CommonListArgs

export type User = {
  id: string;
  username: string;
  email: string;
  photo: Nullable<string>;
  partner: Nullable<PartnerShort>;
  role: RolesEnum;
  joined: IsoDate;
  last_activity: IsoDate;
}
